import React, { useEffect, useState } from "react";
import { AlarmClock, Pencil, Trash2, Check, X, ChevronDown, ChevronUp, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

// "2026-09-18T15:00:00-04:00" -> "2026-09-18T15:00" (value for <input type=datetime-local>)
const toLocalInput = (iso) => (iso || "").slice(0, 16);

function ReminderRow({ r, user, t, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(r.text);
  const [when, setWhen] = useState(toLocalInput(r.due_local));
  const [busy, setBusy] = useState(false);
  const recurring = !!r.repeat;

  const save = async () => {
    const clean = text.trim();
    if (!clean) return;
    setBusy(true);
    try {
      const body = { user_id: user.id, text: clean };
      if (!recurring && when && when !== toLocalInput(r.due_local)) body.when_iso = when; // NY local time
      await api.put(`/buddy/reminders/${r.id}`, body);
      toast.success(t("buddy_mem_saved"));
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "in_past" ? t("buddy_rem_in_past") : t("something_wrong"));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/buddy/reminders/${r.id}`, { params: { user_id: user.id } });
      toast.success(t("buddy_rem_deleted"));
      onChanged();
    } catch (e) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border bg-background px-2.5 py-2" data-testid="buddy-reminder-row">
      {editing ? (
        <div className="space-y-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={300} autoFocus
            className="h-8 text-sm" data-testid="buddy-reminder-edit-text" />
          {recurring ? (
            <div className="text-[11px] text-muted-foreground">{t("buddy_rem_recurring_hint")}</div>
          ) : (
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
              className="h-8 w-full rounded-md border bg-background px-2 text-sm" data-testid="buddy-reminder-edit-when" />
          )}
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" className="h-8" onClick={() => { setEditing(false); setText(r.text); setWhen(toLocalInput(r.due_local)); }} data-testid="buddy-reminder-cancel"><X className="h-4 w-4" /></Button>
            <Button size="sm" className="h-8" onClick={save} disabled={busy || !text.trim()} data-testid="buddy-reminder-save"><Check className="h-4 w-4 mr-1" />{t("save")}</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-sm leading-snug break-words" data-testid="buddy-reminder-text">{r.text}</div>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1 flex-wrap mt-0.5" data-testid="buddy-reminder-next">
              <AlarmClock className="h-3 w-3" /> {r.next}
              {recurring && <span className="inline-flex items-center gap-0.5 rounded bg-muted px-1"><Repeat className="h-3 w-3" /> {r.repeat_label}</span>}
            </div>
          </div>
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setEditing(true)} data-testid="buddy-reminder-edit"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" onClick={remove} disabled={busy} data-testid="buddy-reminder-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      )}
    </div>
  );
}

/** Settings card: the user's active Buddy reminders — edit text/time, cancel. */
export const BuddyRemindersCard = ({ user, t, defaultOpen = false }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [items, setItems] = useState(null);

  const load = () => {
    api.get("/buddy/reminders", { params: { user_id: user.id } })
      .then((r) => setItems(r.data?.reminders || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rounded-lg border p-3.5" data-testid="settings-buddy-reminders">
      <button type="button" className="w-full flex items-center gap-3 text-left" onClick={() => setOpen((v) => !v)} data-testid="settings-buddy-reminders-toggle">
        <AlarmClock className="h-5 w-5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{t("buddy_rem_title")}</div>
          <div className="text-xs text-muted-foreground">{t("buddy_rem_hint")}</div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {items === null && <div className="text-xs text-muted-foreground text-center py-3">…</div>}
          {items !== null && items.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-3" data-testid="buddy-reminder-empty">{t("buddy_rem_empty")}</div>
          )}
          {(items || []).map((r) => <ReminderRow key={r.id} r={r} user={user} t={t} onChanged={load} />)}
        </div>
      )}
    </div>
  );
};
